/* 购物车订单备注 —— snippets/cart-note.liquid。抽屉和 /cart 共用。
 *
 * 写入时机（为什么不边打边存见 snippet 文件头）：
 *   Save / Ctrl(⌘)+Enter / 焦点离开整个备注区 / 收起面板 / 结算前 price.js 调 flush()。
 *   写入直接走 window.fetch：cart-flow 的包装会把它排进购物车队列（按发出顺序串行），结算的 CartFlow.idle() 会等它写完；
 *   flush() 必须同步发出请求（中间不能 await），否则 price.js 随后 setCheckout(true) 会把它拒掉。
 *
 * 重绘：抽屉每次刷新是整段 innerHTML 替换（元素是新的，connectedCallback 重跑）；
 * /cart 是 patchChildren 原地打补丁（元素还是旧的，但 open/hidden/文字会被改回服务端那份）。
 * 所以状态都放在模块级，两种重绘后都由 sync() 补回去。
 *
 * 备注只有一份（Shopify 的 cart.note），所以内容状态（已存 / 草稿 / 正在写）全页共用一份：
 * /cart 页上抽屉和页面里的备注框是同一段文字的两个视图，在哪边改另一边跟着变。
 * 各存各的会出事：一边存失败留着旧草稿、另一边写了新的，结算时两个都 flush，后写的旧草稿把新的盖掉（2026-10-06 评审）。
 * 只有展开、焦点、光标这些界面状态按位置（drawer / page）分开。
 *
 * 内容原样保存（首尾空格、换行都留着），只有"全是空白"当成没写。
 */
(function () {
  if (customElements.get('cart-note')) return;

  const note = {
    saved: null,      // 最近一次确认存进购物车的备注（首屏取 Liquid 输出的值）
    savedSeq: 0,      // saved 来自第几次写入：晚到的旧响应不能盖掉新的
    draft: null,      // 还没存的文字；null = 和 saved 一致
    seq: 0,
    latest: null,     // 最近一次发出、还没回来的写入 {seq, value, promise}
    justSaved: false, // 展开状态下刚存好：Save 显示 "Saved"，再改一个字就恢复
    undo: null,       // Clear 之前的内容，可撤销
    error: ''
  };
  // 界面状态按位置分：抽屉收着不代表 /cart 页的也收着
  const views = {};
  const viewFor = context => (views[context] ||= {
    open: false,
    focusTarget: null, // 焦点在组件里的哪个控件（'input' / 'toggle' / 'save' / 'clear'），重绘后还给新元素
    caret: null        // 输入框的 {start, end, scrollTop}
  });

  // textarea 的 value 里换行永远是 \n；从 Liquid / cart.js 来的可能带 \r\n
  const ANNOUNCE_MS = 3000;
  const clean = value => String(value ?? '').replace(/\r\n?/g, '\n');
  // 存进购物车的值：原样，只有全是空白时存空
  const toStore = value => (clean(value).trim() ? clean(value) : '');
  const text = () => (note.draft !== null ? note.draft : note.saved);
  // "有没有改"要跟购物车最终会是什么比：有写入在路上时是最后发出的那份，不是还没更新的 saved。
  // 否则 A 在路上时改回原文，看着和 saved 一样不算改，结算 flush 只等 A，购物车停在 A（2026-10-06 评审）
  const isDirty = () => toStore(text()) !== (note.latest ? note.latest.value : note.saved);
  // 用户改了字之后记下来。只有"没有写入在路上"且和已存的一致时才能清成 null（跟随 saved）：
  // 写入期间撤回（打 A 点 Save、响应回来前删空 / 改回原文），这时 saved 还是旧值，看着"一致"，
  // 清成 null 的话 A 一回来 saved 变成 A，输入框被填回 A、结算也带 A（2026-10-06 评审）。
  const setDraft = value => { note.draft = !note.latest && value === note.saved ? null : value; };
  // 焦点是因为去点结算才离开备注框的：这时不自动保存，交给结算的 flush()（它本来就会先写备注再建单）。
  // 在这一刻自动保存会触发抽屉整段重绘，正好落在 Xcotton"等加运费保障 → 点旧按钮"的空档里，
  // 旧按钮已被换掉 → 点击走了 VO 的建单（#D15671：优惠码丢失、选项拆行）。price.js 也接住了旧按钮的点击，这里是少一次没必要的重绘。
  const CHECKOUT_SELECTOR = 'button[name="checkout"], [data-xmh-checkout], .xcotton-checkout, .xcotton-checkout-plus, .xcotton-checkout-without';
  let checkoutIntentAt = 0;
  document.addEventListener('pointerdown', event => {
    if (event.target.closest?.(CHECKOUT_SELECTOR)) checkoutIntentAt = Date.now();
  }, true);
  const headingToCheckout = () => Date.now() - checkoutIntentAt < 1500
    || !!document.activeElement?.closest?.(CHECKOUT_SELECTOR) || !!window.CartFlow?.checkout;
  const instances = () => [...document.querySelectorAll('cart-note')].filter(el => typeof el.sync === 'function' && el.input);
  const syncAll = except => instances().forEach(el => { if (el !== except) el.sync(); });

  class CartNote extends HTMLElement {
    connectedCallback() {
      this.context = this.dataset.context || 'drawer';
      this.max = Number(this.dataset.max) || 500;
      this.view = viewFor(this.context);
      this.panel = this.querySelector('[data-cart-note-panel]');
      this.input = this.querySelector('textarea[name="note"]');
      this.count = this.querySelector('[data-cart-note-count]');
      this.clearButton = this.querySelector('[data-cart-note-clear]');
      this.saveButton = this.querySelector('[data-cart-note-save]');
      this.titleText = this.querySelector('[data-cart-note-title]');
      this.preview = this.querySelector('[data-cart-note-preview]');
      this.message = this.querySelector('[data-cart-note-message]');
      // 文案都来自主题编辑器（snippets/cart-note.liquid 写在 data-* 上），这里只留兜底
      const d = this.dataset;
      this.copy = {
        titleEmpty: d.titleEmpty || 'Add an order note', titleSaved: d.titleSaved || 'Order note',
        save: d.labelSave || 'Save', saved: d.labelSaved || 'Saved', clear: d.labelClear || 'Clear', undo: d.labelUndo || 'Undo',
        saveError: d.msgSaveError || 'Your note could not be saved. Please try again.',
        offline: d.msgOffline || 'Your note could not be saved. Please check your connection and try again.',
        tooLong: d.msgTooLong || 'Please shorten your note to ' + (Number(d.max) || 500) + ' characters or fewer.',
        noteSaved: d.msgSaved || 'Order note saved.',
        noteRemoved: d.msgRemoved || 'Order note removed.'
      };
      if (!this.panel || !this.input) return;
      if (note.saved === null) note.saved = toStore(this.input.defaultValue);

      this.onRendered = event => {
        // 购物车是服务端真相：别处（另一个标签页）改过备注，这里跟着变。自己还有写入没回来时不信它，等写入结果
        if (event.detail && 'note' in event.detail && !note.latest) {
          note.saved = toStore(event.detail.note);
          if (note.draft !== null && toStore(note.draft) === note.saved) note.draft = null;
        }
        this.sync();
      };
      this.onBusy = () => this.syncDisabled();
      // 光标位置随时记：重绘删掉旧输入框时，浏览器不保证还有机会（focusout / disconnectedCallback）读到选区
      this.onSelectionChange = () => {
        if (document.activeElement !== this.input) return;
        this.view.caret = { start: this.input.selectionStart, end: this.input.selectionEnd, scrollTop: this.input.scrollTop };
      };
      document.addEventListener('cart:rendered', this.onRendered);
      document.addEventListener('cart:busy', this.onBusy);
      document.addEventListener('selectionchange', this.onSelectionChange);

      if (!this.bound) {
        this.bound = true;
        this.addEventListener('input', event => {
          if (event.target !== this.input) return;
          const value = clean(this.input.value);
          setDraft(value);
          note.justSaved = false;
          if (value) note.undo = null;
          if (note.error) note.error = '';
          this.setError('');
          this.syncControls();
          syncAll(this); // 另一个位置的备注框跟着显示同一段字
        });
        this.addEventListener('keydown', event => {
          if (event.target === this.input && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            this.save({ collapse: true });
          }
        });
        // Safari 点按钮不给按钮焦点，focusout 的 relatedTarget 是 null，分不清"点了 Clear"和"点了外面"：
        // 按下时先记一笔，失焦判断时跳过
        this.addEventListener('pointerdown', () => {
          this.pointerInside = true;
          clearTimeout(this.pointerTimer);
          this.pointerTimer = setTimeout(() => { this.pointerInside = false; }, 600);
        });
        // selectionchange 在老 Safari 上不报 textarea 里的光标移动，键盘/指针/滚动再各补一次
        ['input', 'keyup', 'pointerup', 'select', 'scroll'].forEach(type => this.input.addEventListener(type, () => this.onSelectionChange()));
        this.addEventListener('focusin', event => {
          this.view.focusTarget = this.roleOf(event.target);
        });
        this.addEventListener('focusout', event => {
          const fromInput = event.target === this.input;
          if (fromInput) this.view.caret = { start: this.input.selectionStart, end: this.input.selectionEnd, scrollTop: this.input.scrollTop };
          // 等一拍再判断：控件被重绘删掉时也可能失焦，那不是用户走开了 —— 焦点要还给新元素，也不该触发写入
          setTimeout(() => {
            if (!this.isConnected || !event.target.isConnected) return;
            if (this.contains(document.activeElement)) return;
            this.view.focusTarget = null;
            if (!fromInput || this.pointerInside || !isDirty()) return;
            if (!headingToCheckout()) { this.save({ quiet: true }); return; }
            // 正在去结算：先不写。几秒后还没进入结算（只是把焦点移到了按钮上、或插件那边没走下去），再补存
            setTimeout(() => {
              if (isDirty() && !note.latest && !window.CartFlow?.checkout) this.live()?.save({ quiet: true });
            }, 4000);
          }, 0);
        });
        // 用户点开关时当场记下意图。不能等 toggle 事件再推断：/cart 是原地打补丁，补丁会把 open 属性改回服务端那份，
        // 如果补丁正好落在点击和 toggle 事件之间，toggle 里看到的已经是"收起"，用户这一下就白点了（2026-10-06 手机 /cart 实测）
        this.panel.querySelector('summary')?.addEventListener('click', () => {
          this.userToggle = true;
          this.view.open = !this.panel.open;
        });
        this.panel.addEventListener('toggle', () => {
          if (!this.userToggle) {
            // 不是用户点的：sync()/collapse() 改的（和 view 一致），或者是重绘补丁改的（不一致）—— 后者改回去
            if (this.panel.open !== this.view.open) this.panel.open = this.view.open;
            return;
          }
          this.userToggle = false;
          if (this.panel.open !== this.view.open) this.panel.open = this.view.open;
          if (this.view.open) {
            this.input.focus({ preventScroll: true });
            const end = this.input.value.length;
            this.input.setSelectionRange(end, end);
          } else if (isDirty()) {
            this.save({ quiet: true });
          }
        });
        this.clearButton?.addEventListener('click', () => this.clearOrUndo());
        this.saveButton?.addEventListener('click', () => this.save({ collapse: true, fromButton: true }));
      }
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:rendered', this.onRendered);
      document.removeEventListener('cart:busy', this.onBusy);
      document.removeEventListener('selectionchange', this.onSelectionChange);
    }

    // 同一位置当前挂在页面上的那个实例（抽屉重绘后 this 可能已经是被换掉的旧元素）
    live() {
      if (this.isConnected) return this;
      return instances().find(el => el.context === this.context) || null;
    }

    roleOf(node) {
      if (node === this.input) return 'input';
      if (node === this.saveButton) return 'save';
      if (node === this.clearButton) return 'clear';
      if (node?.closest?.('summary')) return 'toggle';
      return null;
    }

    restoreFocus() {
      const view = this.view;
      if (!view.focusTarget || !this.isConnected || this.contains(document.activeElement)) return;
      let target = this.panel.querySelector('summary');
      if (view.open) {
        if (view.focusTarget === 'input') target = this.input;
        else if (view.focusTarget === 'save') target = this.saveButton;
        else if (view.focusTarget === 'clear') target = this.clearButton?.hidden ? this.input : this.clearButton;
      }
      target?.focus({ preventScroll: true });
      if (target === this.input && view.caret) {
        const { start, end, scrollTop } = view.caret;
        try { this.input.setSelectionRange(start, end); } catch (_) { /* 内容变短时越界 */ }
        this.input.scrollTop = scrollTop;
      }
    }

    // 把状态画回 DOM。两种重绘之后、每次状态变化都走这里。
    sync() {
      const current = text();
      if (clean(this.input.value) !== current) this.input.value = current;
      if (this.panel.open !== this.view.open) this.panel.open = this.view.open;
      if (this.titleText) this.titleText.textContent = note.saved ? this.copy.titleSaved : this.copy.titleEmpty;
      if (this.preview) this.preview.textContent = note.saved;
      this.renderMessage();
      this.syncControls();
      this.restoreFocus();
    }

    syncControls() {
      const length = this.input.value.length;
      if (this.count) {
        this.count.textContent = length + ' / ' + this.max;
        this.count.dataset.level = length >= this.max ? 'limit' : length >= this.max * 0.9 ? 'near' : '';
      }
      if (this.clearButton) {
        const canUndo = !length && note.undo !== null;
        this.clearButton.hidden = !length && !canUndo;
        this.clearButton.textContent = canUndo ? this.copy.undo : this.copy.clear;
      }
      if (this.saveButton) {
        const saving = !!note.latest;
        const done = note.justSaved && !isDirty() && !saving;
        this.saveButton.dataset.state = saving ? 'saving' : done ? 'saved' : '';
        this.saveButton.textContent = done ? this.copy.saved : this.copy.save;
        this.saveButton.setAttribute('aria-busy', String(saving));
      }
      this.syncDisabled();
    }

    // 结算锁住购物车时，输入框由 cart-flow 统一禁用（它认 cart-note textarea），按钮这里跟着禁
    syncDisabled() {
      const locked = !!window.CartFlow?.checkout;
      [this.clearButton, this.saveButton].forEach(button => { if (button) button.disabled = locked; });
    }

    // 提示区：报错（看得见）优先；没有报错时放最近一次"已保存/已删除"（只给读屏）。
    // 播报内容放在共享状态里：保存后抽屉马上整段重绘，新的提示区是空的，不补回去的话读屏可能还没念就没了。
    // 只在发起保存的那个位置播，/cart 页上抽屉和页面两个框不会各播一遍。
    renderMessage() {
      if (!this.message) return;
      const said = note.announcement;
      const fresh = said && said.context === this.context && Date.now() - said.at < ANNOUNCE_MS;
      const text = note.error || (fresh ? said.text : '');
      const tone = note.error ? 'error' : fresh ? 'success' : '';
      if (this.message.dataset.tone !== tone) this.message.dataset.tone = tone;
      if (this.message.textContent !== text) this.message.textContent = text;
    }

    setError(message) {
      note.error = message || '';
      this.renderMessage();
    }

    announce(message) {
      if (!this.message || note.error) return;
      const said = { text: message, context: this.context, at: Date.now() };
      note.announcement = said;
      // 先清空再写，同一句连着两次也会重新播
      this.message.textContent = '';
      requestAnimationFrame(() => this.live()?.renderMessage());
      // 只清自己这条：连续保存/清除时，上一条的定时器不能把新的一条提前清掉
      setTimeout(() => { if (note.announcement === said) { note.announcement = null; syncAll(); } }, ANNOUNCE_MS);
    }

    clearOrUndo() {
      if (!this.input.value && note.undo !== null) {
        this.input.value = note.undo;
        note.undo = null;
      } else {
        note.undo = clean(this.input.value);
        this.input.value = '';
      }
      setDraft(clean(this.input.value));
      note.justSaved = false;
      note.error = '';
      this.setError('');
      this.syncControls();
      syncAll(this);
      this.input.focus({ preventScroll: true });
    }

    // 结算前由 price.js 调（页面上每个备注框都会调一次，内容只有一份，重复的会合并成同一次写入）。
    // 必须同步发出请求，见文件头。
    flush() {
      if (!this.input) return Promise.resolve(true);
      if (!isDirty()) return note.latest ? note.latest.promise : Promise.resolve(true);
      return this.save({ quiet: true });
    }

    save({ collapse = false, quiet = false, fromButton = false } = {}) {
      const value = toStore(text());
      if (value.length > this.max) {
        note.error = this.copy.tooLong;
        syncAll();
        if (!quiet) this.live()?.input.focus();
        return Promise.resolve(false);
      }
      // 只有"当前文字已经确认存好"才收起。写入期间又改了字，旧请求成功也不能收：
      // 收起后新字只在内存里，刷新就丢（2026-10-06 评审）。这时接着把新字存掉再收。
      const finish = ok => {
        if (!ok || !collapse) return ok;
        if (isDirty()) return this.save({ collapse, quiet, fromButton });
        this.live()?.collapse(fromButton);
        return ok;
      };
      if (note.latest && note.latest.value === value) return note.latest.promise.then(finish);
      if (!note.latest && value === note.saved) {
        note.draft = null;
        return Promise.resolve(finish(true));
      }
      note.error = '';
      note.undo = null;
      const seq = ++note.seq;
      let request;
      try {
        request = fetch(window.CartFlow ? CartFlow.url('cart/update.js') : '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value })
        });
      } catch (error) {
        request = Promise.reject(error);
      }
      const entry = { seq, value, promise: null };
      entry.promise = request.then(response => {
        if (!response.ok) throw Object.assign(new Error('note rejected'), { rejected: true });
      }).then(() => {
        // 购物车队列按发出顺序写，序号大的才是购物车里最后的值
        if (seq > note.savedSeq) {
          note.saved = value;
          note.savedSeq = seq;
        }
        if (note.latest === entry) note.latest = null;
        // 还有更新的写入在路上就先留着草稿，等最后一个回来再对；对上了才跟随 saved
        if (note.draft !== null && !note.latest && toStore(note.draft) === note.saved) note.draft = null;
        note.justSaved = !collapse && !isDirty();
        syncAll();
        const live = this.live();
        live?.announce(value ? live.copy.noteSaved : live.copy.noteRemoved);
        return true;
      }, error => {
        if (note.latest === entry) note.latest = null;
        // 草稿还在 note.draft 里没动；展开面板让人看见报错和没存上的字
        if (note.draft === null && value !== note.saved) note.draft = value;
        const live = this.live();
        if (live) live.view.open = true;
        // 提示语都用主题编辑器里配的：只分"连不上"和"没存上"两种，不把 cart-flow / Shopify 的英文原话直接给客户看
        const copy = (live || this).copy;
        // 网络失败经过 cart-flow 时会被换成它自己的英文（"could not reach the store" / "could not confirm the last change"）
        const offline = !error?.rejected && (error instanceof TypeError || navigator.onLine === false
          || /Failed to fetch|NetworkError|could not reach|could not confirm|timed out/i.test(error?.message || ''));
        note.error = offline ? copy.offline : copy.saveError;
        syncAll();
        return false;
      });
      note.latest = entry;
      syncAll();
      return entry.promise.then(finish);
    }

    collapse(returnFocus) {
      this.view.open = false;
      note.justSaved = false;
      this.panel.open = false;
      // 键盘点 Save：按钮随面板收起看不见了，焦点交给开关那一行
      if (returnFocus && (this.contains(document.activeElement) || document.activeElement === document.body)) {
        this.panel.querySelector('summary')?.focus({ preventScroll: true });
      }
      this.syncControls();
    }
  }

  customElements.define('cart-note', CartNote);
})();
